'use client';

// CollapsibleUploadPreview - Wrapper that collapses upload previews to reduce duplication with unified panel

import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslations } from 'next-intl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export interface CollapsibleUploadPreviewProps {
  /** Number of files currently in preview */
  filesCount: number;
  /** Number of images currently in preview */
  imagesCount: number;
  /** Number of URLs currently in preview */
  urlsCount: number;
  /** Number of Google docs currently in preview */
  googleDocsCount: number;
  /** Whether any items are currently being uploaded/processed */
  isProcessing: boolean;
  /** The preview list components to render inside the collapsible */
  children: ReactNode;
}

/**
 * CollapsibleUploadPreview - Wraps upload preview lists with a toggle
 *
 * When items are uploaded, they appear both in the preview lists under the input
 * and in the unified content panel. This causes visual duplication.
 * This component collapses the preview lists by default once items exist,
 * showing just a summary with a toggle to expand.
 *
 * Features:
 * - Auto-collapses when items exist and not processing
 * - Shows count summary chips by type
 * - Expands automatically when processing new items
 * - RTL support via CSS logical properties
 */
export function CollapsibleUploadPreview({
  filesCount,
  imagesCount,
  urlsCount,
  googleDocsCount,
  isProcessing,
  children,
}: CollapsibleUploadPreviewProps) {
  const t = useTranslations('creator.uploadPreview');
  const totalCount = filesCount + imagesCount + urlsCount + googleDocsCount;

  // Always start collapsed - user can expand if needed
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't render anything if no items
  if (totalCount === 0) {
    return null;
  }

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header with toggle and summary */}
      <Box
        onClick={toggleExpanded}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 1.5,
          cursor: 'pointer',
          borderRadius: 1,
          backgroundColor: 'action.hover',
          '&:hover': {
            backgroundColor: 'action.selected',
          },
        }}
      >
        <IconButton
          size="small"
          aria-label={isExpanded ? t('collapse') : t('expand')}
          sx={{ p: 0.5 }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        {/* Uploading indicator */}
        {isProcessing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
            }}
          >
            <CircularProgress size={16} color="inherit" />
            <CloudUploadIcon sx={{ fontSize: 18 }} />
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {isProcessing ? t('uploading') : t('recentUploads', { count: totalCount })}
        </Typography>

        {/* Type chips */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {filesCount > 0 && (
            <Chip
              label={filesCount}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {imagesCount > 0 && (
            <Chip
              label={imagesCount}
              size="small"
              color="success"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {urlsCount > 0 && (
            <Chip
              label={urlsCount}
              size="small"
              color="info"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {googleDocsCount > 0 && (
            <Chip
              label={googleDocsCount}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </Box>

      {/* Collapsible content */}
      <Collapse in={isExpanded}>
        {children}
      </Collapse>
    </Box>
  );
}
